import { Formik, Form } from "formik";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Divider, Grid, Header, Icon, Label, Segment } from "semantic-ui-react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { getAuth } from "firebase/auth";
import {
  addCeCertToDb,
  updateCeCertInDb,
} from "../../../app/firestore/firestoreService";
import { uploadBlobToStorage } from "../../../app/firestore/firebaseService";
import { app } from "../../../app/config/firebase";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import MyTextInput from "../../../app/common/form/MyTextInput";
import MyNumberInput from "../../../app/common/form/MyNumberInput";
import MyDateInput from "../../../app/common/form/MyDateInput";

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/png", "image/jpeg"];
const ACCEPTED_EXTENSIONS = ".pdf,.png,.jpg,.jpeg";

export default function CeCertsEdit({ ceCert }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setFileError("Please upload a PDF, PNG, or JPG file.");
      setSelectedFile(null);
      return;
    }
    if (file.size > 1024 * 1024) {
      setFileError("File must be less than 1MB.");
      setSelectedFile(null);
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  }

  let initialValues = ceCert
    ? ceCert
    : {
        courseName: "",
        numberCredits: "",
        dateCertificate: "",
      };

  const validationSchema = Yup.object({
    courseName: Yup.string().required("You must provide a course name"),
    dateCertificate: Yup.date()
      .typeError("You must provide a valid date")
      .required("You must provide a date of certificate"),
  });

  return (
    <ModalWrapper size="small">
      <Segment clearing>
        <div className="medium horizontal margin small top margin">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                let fileUrl = values.fileUrl || null;
                if (selectedFile) {
                  const auth = getAuth(app);
                  const uid = auth.currentUser.uid;
                  const storagePath = `ceCerts/${uid}/${Date.now()}_${selectedFile.name}`;
                  const blob = await fetch(URL.createObjectURL(selectedFile)).then((r) => r.blob());
                  fileUrl = await uploadBlobToStorage(storagePath, blob);
                }
                const submitValues = { ...values, ...(fileUrl ? { fileUrl } : {}) };
                ceCert
                  ? await updateCeCertInDb(values.id, submitValues)
                  : await addCeCertToDb(submitValues);
                setSubmitting(false);
                toast.success("CE Certificate successfully updated");
                dispatch(closeModal({ modalType: "CeCertsEdit" }));
              } catch (error) {
                toast.error(error.message);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, dirty, isValid, values }) => (
              <Form className="ui form" autoComplete="off">
                <Header size="large" color="blue">
                  {ceCert ? "Edit CE Cert" : "Create CE Cert"}
                </Header>
                <Divider />
                <Grid>
                  <Grid.Row className="small vertical padding">
                    <Grid.Column width={16}>
                      <Header
                        as="h5"
                        color="blue"
                        className="mini bottom margin"
                      >
                        CE Certificate Course Name
                      </Header>
                      <MyTextInput
                        name="courseName"
                        className="small bottom margin"
                      />
                    </Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="small vertical padding">
                    <Grid.Column width={5} className="small bottom margin">
                      <Header
                        as="h5"
                        color="blue"
                        className="small bottom margin"
                      >
                        Number of Credits
                      </Header>
                      <MyNumberInput
                        name="numberCredits"
                        className="small bottom margin"
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <Header
                        as="h5"
                        color="blue"
                        className="small bottom margin"
                      >
                        Date of Certificate
                      </Header>
                      <MyDateInput
                        name="dateCertificate"
                        placeholder="Date of Certificate"
                        dateFormat="MMMM d, yyyy"
                      />
                    </Grid.Column>
                    <Grid.Column width={8}></Grid.Column>
                  </Grid.Row>

                  <Grid.Row className="small vertical padding">
                    <Grid.Column width={16}>
                      <Header as="h5" color="blue" className="mini bottom margin">
                        Certificate File
                      </Header>
                      <input
                        type="file"
                        accept={ACCEPTED_EXTENSIONS}
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                      <Button
                        type="button"
                        basic
                        color="blue"
                        icon="upload"
                        content="Choose File"
                        size="small"
                        onClick={() => fileInputRef.current.click()}
                      />
                      {selectedFile && (
                        <Label basic style={{ marginLeft: "8px" }}>
                          <Icon name="file outline" />
                          {selectedFile.name}
                          <Icon
                            name="delete"
                            link
                            onClick={() => {
                              setSelectedFile(null);
                              fileInputRef.current.value = "";
                            }}
                          />
                        </Label>
                      )}
                      {!selectedFile && ceCert?.fileUrl && (
                        <a
                          href={ceCert.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: "8px" }}
                        >
                          <Icon name="file outline" />
                          View existing file
                        </a>
                      )}
                      {fileError && (
                        <p style={{ color: "red", marginTop: "4px" }}>{fileError}</p>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <Divider className="medium top margin" />
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  type="submit"
                  floated={isMobile ? null : "right"}
                  primary
                  content="Submit"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
                <Button
                  disabled={isSubmitting}
                  onClick={() =>
                    dispatch(
                      closeModal({
                        modalType: "CeCertsEdit",
                      })
                    )
                  }
                  to="#"
                  type="button"
                  floated={isMobile ? null : "right"}
                  content="Cancel"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
              </Form>
            )}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}
